import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Building2, Users, Database, KeyRound, Power, LogIn, Info, ShieldCheck, Columns3, ScrollText, Plug, Image, CreditCard, MessageCircle, Menu as MenuIcon } from "lucide-react";
import { ColumnsManager, UsersManager, AuditManager, IntegrationsManager, PermissionsManager, WhatsAppTemplatesManager } from "./Settings";
import BrandSettings from "../components/BrandSettings";
import PaymentSettings from "../components/PaymentSettings";
import ParasutSettings from "../components/ParasutSettings";
import MenuSettings from "../components/MenuSettings";

const TABS = [
  ["info", "BİLGİLER", Info], ["users", "KULLANICILAR", Users], ["perms", "YETKİLER", ShieldCheck], ["columns", "KOLONLAR", Columns3],
  ["menu", "MENÜ", MenuIcon], ["brand", "LOGO", Image], ["payment", "ÖDEME / PARAŞÜT", CreditCard], ["whatsapp", "WHATSAPP", MessageCircle],
  ["integrations", "ENTEGRASYON", Plug], ["audit", "KAYITLAR", ScrollText],
];

function InfoTab({ firm, reload }) {
  const [name, setName] = useState(firm.name);
  const [pin, setPin] = useState("");
  const put = async (body, msg) => { try { await api.put(`/firms/${firm.id}`, body); toast.success(msg); reload(); } catch (e) { toast.error(e.response?.data?.detail || "HATA"); } };
  const box = "rounded-xl border border-slate-800 bg-slate-900 p-4";
  return (
    <div className="grid max-w-4xl grid-cols-1 gap-3 md:grid-cols-2">
      <div className={box}>
        <div className="mb-2 text-xs font-bold text-slate-400">FİRMA ADI</div>
        <div className="flex gap-2"><Input data-testid="fd-name" value={name} onChange={(e) => setName(e.target.value.toLocaleUpperCase("tr-TR"))} className="h-9 border-slate-700 bg-slate-800 text-slate-100" /><Button data-testid="fd-name-save" onClick={() => put({ name }, "FİRMA ADI GÜNCELLENDİ")} className="bg-blue-600 text-white">KAYDET</Button></div>
      </div>
      <div className={box}>
        <div className="mb-2 text-xs font-bold text-slate-400">DURUM</div>
        <Button data-testid="fd-toggle-active" onClick={() => put({ active: !firm.active }, firm.active ? "FİRMA PASİF YAPILDI" : "FİRMA AKTİF YAPILDI")} className={firm.active ? "bg-emerald-600 text-white" : "bg-rose-700 text-white"}><Power className="mr-1 h-4 w-4" /> {firm.active ? "AKTİF — PASİF YAP" : "PASİF — AKTİF YAP"}</Button>
      </div>
      <div className={box}>
        <div className="mb-2 flex items-center gap-2 text-xs font-bold text-slate-400"><KeyRound className="h-3.5 w-3.5" /> FİRMA PIN {firm.has_pin ? <span className="text-amber-300">(TANIMLI)</span> : <span className="text-slate-500">(YOK)</span>}</div>
        <div className="flex gap-2">
          <Input data-testid="fd-pin" type="password" inputMode="numeric" placeholder="YENİ PIN" value={pin} onChange={(e) => setPin(e.target.value)} className="h-9 border-slate-700 bg-slate-800 text-slate-100" />
          <Button data-testid="fd-pin-save" disabled={!pin} onClick={() => { put({ pin }, "PIN AYARLANDI"); setPin(""); }} className="bg-amber-600 text-white">AYARLA</Button>
          {firm.has_pin && <Button data-testid="fd-pin-clear" variant="outline" onClick={() => put({ pin: "" }, "PIN KALDIRILDI")} className="border-slate-700 bg-slate-800 text-slate-200">KALDIR</Button>}
        </div>
      </div>
      <div className={box}>
        <div className="mb-2 text-xs font-bold text-slate-400">ÖZET</div>
        <div className="flex gap-6 text-sm text-slate-200"><span><Users className="mr-1 inline h-4 w-4 text-slate-500" />{firm.user_count} KULLANICI</span><span><Database className="mr-1 inline h-4 w-4 text-slate-500" />{firm.customer_count} MÜŞTERİ</span></div>
      </div>
    </div>
  );
}

export default function FirmDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [firm, setFirm] = useState(null);
  const [tab, setTab] = useState(() => localStorage.getItem("mwcrm_fd_tab") || "info");
  const pick = (k) => { setTab(k); localStorage.setItem("mwcrm_fd_tab", k); };
  const load = () => api.get("/firms").then((r) => setFirm(r.data.find((f) => f.id === id) || null));
  useEffect(() => { if (localStorage.getItem("mwcrm_tenant") !== id) { localStorage.setItem("mwcrm_tenant", id); window.location.reload(); return; } load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!isSuperAdmin) return null;
  if (!firm) return <div className="p-10 text-center text-slate-500">YÜKLENİYOR…</div>;
  return (
    <div data-testid="firm-detail" className="mw-fade-up flex h-[calc(100vh-90px)] flex-col gap-2">
      <div className="flex shrink-0 items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 p-2">
        <button data-testid="fd-back" onClick={() => nav("/firmalar")} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"><ArrowLeft className="h-4 w-4" /></button>
        <Building2 className="h-5 w-5 text-blue-400" />
        <h1 data-testid="fd-title" className="font-head text-lg font-bold text-white">{firm.name}</h1>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${firm.active ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>{firm.active ? "AKTİF" : "PASİF"}</span>
        <div className="mx-2 flex min-w-0 flex-1 items-center gap-1 overflow-x-auto whitespace-nowrap">
          {TABS.map(([k, l, I]) => (
            <button key={k} data-testid={`fd-tab-${k}`} onClick={() => pick(k)} className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold ${tab === k ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"}`}><I className="h-3.5 w-3.5" />{l}</button>
          ))}
        </div>
        <Button data-testid="fd-enter" size="sm" onClick={() => { window.location.href = "/musteriler"; }} className="bg-blue-600 text-white"><LogIn className="mr-1 h-4 w-4" /> FİRMAYA GİR</Button>
      </div>
      <div data-testid="fd-content" className="min-h-0 flex-1 overflow-y-auto pr-1">
        {tab === "info" && <InfoTab firm={firm} reload={load} />}
        {tab === "users" && <UsersManager />}
        {tab === "perms" && <PermissionsManager />}
        {tab === "columns" && <ColumnsManager />}
        {tab === "menu" && <MenuSettings />}
        {tab === "brand" && <BrandSettings />}
        {tab === "payment" && <div className="space-y-4"><PaymentSettings /><ParasutSettings /></div>}
        {tab === "whatsapp" && <WhatsAppTemplatesManager />}
        {tab === "integrations" && <IntegrationsManager />}
        {tab === "audit" && <AuditManager />}
      </div>
    </div>
  );
}
